import {
  UserDefectInputTypeEnum,
  type DefectItemDefinition,
  type InspectionPeriod,
  type UserDefectInputItem,
} from '@/app/data/type'
import { isTradeKey } from '@/app/function/defectItemEditor'

// 하자진단 과업 설정(사용검사 전/후·하자항목) 공용 로직.
// 저장: tasks.attribute.defect_period / defect_item_ids (품질점검의 inspection_period와 다른 키)
// 적용 범위(2026-11-04 사용자 결정): 표시 목록(결함 패널 선택지·체크리스트·전차 자동완성)만 과업 선택 기준,
// 판정('미등록')·부록 출력은 회사 전체 정의 기준

// 과업 선택·패널 필터에 필요한 정의 요약 (getInputItems 캐시에 함께 보관 → 오프라인에서도 id → 이름 변환)
export type DefectItemSummary = Pick<
  DefectItemDefinition,
  'id' | 'building_type' | 'structure' | 'inspection_period' | 'name' | 'include_by_default'
> & { created_at?: string }

export interface TaskDefectSetting {
  period: InspectionPeriod
  itemIds: number[]
}

export const DEFECT_PERIOD_LABEL: Record<InspectionPeriod, string> = {
  PRE: '사용검사전',
  POST: '사용검사후',
}

export function toDefectItemSummary(item: DefectItemDefinition): DefectItemSummary {
  const { id, building_type, structure, inspection_period, name, include_by_default } = item
  const created_at = (item as DefectItemDefinition & { created_at?: string }).created_at
  return { id, building_type, structure, inspection_period, name, include_by_default, created_at }
}

// attribute에 전/후가 없으면 null (기존 과업 = 현행 동작: 패널 전/후 선택, 출력 둘 다)
export function getTaskDefectSetting(
  task: { attribute?: unknown } | null | undefined,
): TaskDefectSetting | null {
  const attr = task?.attribute as
    | { defect_period?: unknown; defect_item_ids?: unknown }
    | null
    | undefined
  const period = attr?.defect_period
  if (period !== 'PRE' && period !== 'POST') return null
  const ids = Array.isArray(attr?.defect_item_ids)
    ? attr.defect_item_ids.filter((v): v is number => typeof v === 'number')
    : []
  return { period, itemIds: ids }
}

function keyOf(
  buildingType: string | null,
  structure: string | null,
  period: string | null,
): string {
  return `${buildingType ?? ''}|${structure ?? ''}|${period ?? ''}`
}

// 입력항목에서 과업이 고르지 않은 하자항목을 뺀 사본. 정의가 없는 이름(옛 UI로 넣은 값)은 그대로 둔다.
// - 2컬럼: DEFECT(defect NULL) row values에서 제외 → 남은 항목이 0개가 된 위치는 MEMBER values에서도 제외
//   (원래 비어 있던 위치는 유지)
// - 3컬럼(전유 POST): MEMBER values(공종)만 제외
// 과업 시기가 아닌 row는 그대로 둔다(전차 자동완성은 다른 시기 항목도 씀)
export function filterInputDataByTaskItems(
  inputData: UserDefectInputItem[],
  summaries: DefectItemSummary[],
  setting: TaskDefectSetting | null,
): UserDefectInputItem[] {
  if (!setting || summaries.length === 0) return inputData
  const selected = new Set(setting.itemIds)
  const defined = new Map<string, Set<string>>()
  const picked = new Map<string, Set<string>>()
  for (const s of summaries) {
    if (s.inspection_period !== setting.period) continue
    const key = keyOf(s.building_type, s.structure, s.inspection_period)
    if (!defined.has(key)) defined.set(key, new Set())
    defined.get(key)!.add(s.name)
    if (selected.has(s.id)) {
      if (!picked.has(key)) picked.set(key, new Set())
      picked.get(key)!.add(s.name)
    }
  }
  const keep = (key: string, name: string): boolean =>
    !defined.get(key)?.has(name) || !!picked.get(key)?.has(name)

  // 2컬럼 위치별로 비게 된 것 표시
  const emptiedMembers = new Map<string, Set<string>>()
  const result = inputData.map(row => {
    if (!row.structure || !row.inspection_period) return row
    const key = keyOf(row.building_type, row.structure, row.inspection_period)
    if (!defined.has(key)) return row
    const trade = isTradeKey(row.structure, row.inspection_period)
    if (trade && row.type === UserDefectInputTypeEnum.MEMBER) {
      return { ...row, values: row.values.filter(v => keep(key, v.name)) }
    }
    if (!trade && row.type === UserDefectInputTypeEnum.DEFECT && !row.defect) {
      const values = row.values.filter(v => keep(key, v.name))
      if (values.length === 0 && row.values.length > 0 && row.member) {
        if (!emptiedMembers.has(key)) emptiedMembers.set(key, new Set())
        emptiedMembers.get(key)!.add(row.member)
      }
      return { ...row, values }
    }
    return row
  })
  if (emptiedMembers.size === 0) return result
  return result.map(row => {
    if (row.type !== UserDefectInputTypeEnum.MEMBER) return row
    const emptied = emptiedMembers.get(
      keyOf(row.building_type, row.structure, row.inspection_period),
    )
    if (!emptied) return row
    return { ...row, values: row.values.filter(v => !emptied.has(v.name)) }
  })
}

// ── 하자항목 선택 UI(과업 만들기·과업 편집) ──
export interface DefectItemPickGroup {
  structure: 'COMMON' | 'EXCLUSIVE'
  location: string | null // 2컬럼 위치, 3컬럼(공종 목록)은 null
  items: DefectItemSummary[]
}

// 시기 하나의 선택 목록: 공용 → 전유, 위치 순서·위치 안 항목 순서는 입력항목 values 정본.
// 여러 위치에 배치된 항목은 위치마다 나오고 체크는 id 하나를 공유한다.
export function buildDefectItemPickGroups(
  inputData: UserDefectInputItem[],
  summaries: DefectItemSummary[],
  buildingType: string,
  period: InspectionPeriod,
): DefectItemPickGroup[] {
  const groups: DefectItemPickGroup[] = []
  for (const structure of ['COMMON', 'EXCLUSIVE'] as const) {
    const byName = new Map<string, DefectItemSummary>()
    for (const s of summaries) {
      if (
        s.building_type === buildingType &&
        s.structure === structure &&
        s.inspection_period === period
      ) {
        byName.set(s.name, s)
      }
    }
    if (byName.size === 0) continue
    const rows = inputData.filter(
      r =>
        r.building_type === buildingType &&
        r.structure === structure &&
        r.inspection_period === period,
    )
    const memberNames =
      rows.find(r => r.type === UserDefectInputTypeEnum.MEMBER)?.values.map(v => v.name) ?? []
    const pick = (names: string[]): DefectItemSummary[] =>
      names.map(n => byName.get(n)).filter((s): s is DefectItemSummary => !!s)

    if (isTradeKey(structure, period)) {
      const items = pick(memberNames)
      if (items.length > 0) groups.push({ structure, location: null, items })
      continue
    }
    for (const member of memberNames) {
      const row = rows.find(
        r => r.type === UserDefectInputTypeEnum.DEFECT && r.member === member && !r.defect,
      )
      const items = pick(row?.values.map(v => v.name) ?? [])
      if (items.length > 0) groups.push({ structure, location: member, items })
    }
  }
  return groups
}

// 새 과업 선체크 = 그 시기의 기본(★) 항목
export function defaultDefectItemIds(
  summaries: DefectItemSummary[],
  buildingType: string,
  period: InspectionPeriod,
): number[] {
  return summaries
    .filter(
      s =>
        s.building_type === buildingType && s.inspection_period === period && s.include_by_default,
    )
    .map(s => s.id)
}

// 결함이 쓰는 이름 → 정의 id (기존 과업 전환 시 쓰던 항목이 사라지지 않게 선체크에 더함)
export function usedDefectItemIds(
  summaries: DefectItemSummary[],
  buildingType: string,
  period: InspectionPeriod,
  defects: {
    template?: string | null
    classification?: string | null
    repair_plan?: string | null
  }[],
): number[] {
  const ids = new Set<number>()
  for (const d of defects) {
    const structure =
      d.template === 'defect-EXCLUSIVE'
        ? 'EXCLUSIVE'
        : d.template === 'defect-COMMON'
          ? 'COMMON'
          : null
    if (!structure) continue
    const name = (isTradeKey(structure, period) ? d.repair_plan : d.classification)?.trim()
    if (!name) continue
    const hit = summaries.find(
      s =>
        s.building_type === buildingType &&
        s.structure === structure &&
        s.inspection_period === period &&
        s.name === name,
    )
    if (hit) ids.add(hit.id)
  }
  return [...ids]
}
